'use client';

import { useIsMobile } from '@/hooks/useWindowDimensions';
import { ComboboxTrigger } from '@heidi/design-system/combobox';
import { DrawerHeader, DrawerTitle } from '@heidi/design-system/drawer';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@heidi/design-system/select';
import { TypographyP2 } from '@heidi/design-system/typography';
import {
  PopoverDrawer,
  PopoverDrawerContent,
  PopoverDrawerTrigger,
} from '@heidi/snowflake-ui/mobile-responsive-popover-drawer';
import { Check } from 'lucide-react';
import { useState } from 'react';

type Option = {
  value: string;
  label: string;
};

type ResponsiveSelectProps = {
  id?: string;
  value: string;
  onValueChange: (value: string) => void;
  options: Option[];
  placeholder: string;
  drawerTitle?: string;
  className?: string;
};

export function ResponsiveSelect({
  id,
  value,
  onValueChange,
  options,
  placeholder,
  drawerTitle,
  className,
}: ResponsiveSelectProps) {
  const isMobile = useIsMobile();
  const [open, setOpen] = useState(false);

  if (!isMobile) {
    return (
      <Select value={value} onValueChange={onValueChange}>
        <SelectTrigger id={id} className={className}>
          <SelectValue placeholder={placeholder} />
        </SelectTrigger>
        <SelectContent>
          {options.map((opt) => (
            <SelectItem key={opt.value} value={opt.value}>
              {opt.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    );
  }

  const selected = options.find((opt) => opt.value === value);

  return (
    <PopoverDrawer open={open} onOpenChange={setOpen}>
      <PopoverDrawerTrigger asChild>
        <ComboboxTrigger
          open={open}
          id={id}
          isPlaceholder={!selected}
          role="combobox"
          className={className}
        >
          <TypographyP2 className="truncate text-left">
            {selected?.label ?? placeholder}
          </TypographyP2>
        </ComboboxTrigger>
      </PopoverDrawerTrigger>
      <PopoverDrawerContent className="px-0 pb-4">
        <DrawerHeader>
          <DrawerTitle>{drawerTitle ?? placeholder}</DrawerTitle>
        </DrawerHeader>
        <div className="flex flex-col">
          {options.map((opt) => (
            <button
              key={opt.value}
              type="button"
              onClick={() => {
                onValueChange(opt.value);
                setOpen(false);
              }}
              className="flex items-center justify-between gap-3 px-4 py-3 text-left hover:bg-background-secondary"
            >
              <TypographyP2>{opt.label}</TypographyP2>
              {value === opt.value && (
                <Check className="size-4 shrink-0 text-text-primary" />
              )}
            </button>
          ))}
        </div>
      </PopoverDrawerContent>
    </PopoverDrawer>
  );
}
